import { DateTime } from 'luxon';
import RequestStatusType from './enums/RequestStatusType';
import RequestStatusTitle from './enums/RequestStatusTitle';
import RequestStatusColor from './enums/RequestStatusColor';
import { DateLainer } from './vue/mixins/DateLainer';
import { priorityTitle } from './requestPriority';

/**
 * Связь обращений «главное → связанные» (массовая проблема): что показывать и кому.
 *
 * Решения вынесены из компонентов, чтобы проверяться тестами без монтирования.
 *
 * Доступ здесь не вычисляется: список связанных бэкенд уже сузил до тех, что сотрудник
 * может открыть, а обычному пользователю номера других обращений не приходят вовсе —
 * только анонимный флаг hasParentRequest у его собственного.
 */

export const EMPTY_VALUE = '—';

/**
 * Анонимное сообщение для пользователя: без номера и без ссылки.
 */
export const ANONYMOUS_LINK_MESSAGE = 'Обращение связано с массовой проблемой.';

/**
 * Сотрудник: исполнитель или администратор. Только им видны номера связанных обращений,
 * вкладка «Связанные обращения» и управление связями.
 *
 * @param {{isExecutor: boolean, isAdmin: boolean}} roles
 * @returns {boolean}
 */
export function isStaff(roles) {
    return !!roles && (roles.isExecutor === true || roles.isAdmin === true);
}

/**
 * Что показать в карточке о главном обращении.
 *
 *   null                 — обращение ни с чем не связано;
 *   { anonymous: true }  — связано, но номер показывать нельзя: пользователю никогда,
 *                          сотруднику — если доступа к главному обращению у него нет;
 *   { parentId: 1500 }   — «Связано с обращением №1500» со ссылкой.
 *
 * @param {Object} request Обращение из карточки (hasParentRequest приходит всем).
 * @param {Object|null} related Ответ GET /requests/{id}/related (только сотрудникам).
 * @param {{isExecutor: boolean, isAdmin: boolean}} roles
 * @returns {Object|null}
 */
export function parentLinkView(request, related, roles) {
    const linked = !!(request && request.hasParentRequest);

    if (!linked) {
        return null;
    }

    if (isStaff(roles) && related && related.parent && related.parent.id) {
        return { anonymous: false, parentId: related.parent.id };
    }

    return { anonymous: true, message: ANONYMOUS_LINK_MESSAGE };
}

/**
 * Показывать ли вкладку «Связанные обращения».
 *
 * Только сотрудникам и только в обращении, которое может быть главным: связанное само
 * главным стать не может, пока его не отвяжут (цепочек глубже одного уровня нет).
 *
 * @param {Object} request
 * @param {{isExecutor: boolean, isAdmin: boolean}} roles
 * @returns {boolean}
 */
export function canShowRelatedTab(request, roles) {
    return isStaff(roles) && !!request && !request.hasParentRequest;
}

function parseServerDate(date) {
    return DateTime.fromSQL(String(date), { zone: DateLainer.SERVER_TIMEZONE });
}

/**
 * Дата в часовом поясе клиента: «03.10, 16:00».
 *
 * @param {string} date Дата сервера в SQL-формате.
 * @returns {string}
 */
export function formatShortDate(date) {
    const parsed = parseServerDate(date);

    return parsed.isValid ? parsed.setZone(DateLainer.CLIENT_TIMEZONE).toFormat('dd.LL, HH:mm') : EMPTY_VALUE;
}

/**
 * Срок в строке связанного обращения: дата или понятное состояние.
 *
 * @param {Object} request
 * @param {{now?: number, format?: Function}} options
 * @returns {{text: string, overdue: boolean}}
 */
export function relatedDeadline(request, { now = Date.now(), format = formatShortDate } = {}) {
    switch (request.status) {
        case RequestStatusType.WORKUP: {
            if (!request.deadline) {
                return { text: EMPTY_VALUE, overdue: false };
            }

            const overdue = parseServerDate(request.deadline).toMillis() <= now;

            return {
                text: overdue ? `просрочено, срок ${format(request.deadline)}` : `до ${format(request.deadline)}`,
                overdue,
            };
        }
        case RequestStatusType.PAUSED:
            return {
                text: request.resumeAt ? `приостановлено до ${format(request.resumeAt)}` : 'приостановлено',
                overdue: false,
            };
        case RequestStatusType.REWORK:
            return { text: 'ждём ответа автора', overdue: false };
        default:
            // Выполнено, отказано, закрыто: срок больше не идёт.
            return { text: 'срок не идёт', overdue: false };
    }
}

/**
 * Исполнитель, а если он не назначен — группа ответственных.
 *
 * @param {Object} request
 * @returns {string}
 */
export function assigneeName(request) {
    if (request.executor && request.executor.displayName) {
        return request.executor.displayName;
    }

    if (request.securityGroup && request.securityGroup.name) {
        return request.securityGroup.name;
    }

    return EMPTY_VALUE;
}

/**
 * Строка связанного обращения во вкладке. Только то, что нужно сотруднику для навигации:
 * номер, приоритет, статус, исполнитель/группа, срок и признак непрочитанного. Автора,
 * город и время последней активности строка не показывает намеренно.
 *
 * @param {Object} request Обращение из ответа GET /requests/{id}/related.
 * @param {{now?: number, format?: Function}} options
 * @returns {Object}
 */
export function relatedRow(request, options = {}) {
    const deadline = relatedDeadline(request, options);

    return {
        id: request.id,
        number: `№${request.id}`,
        priority: request.priority,
        priorityTitle: priorityTitle(request.priority),
        statusTitle: RequestStatusTitle[request.status] || EMPTY_VALUE,
        statusColor: RequestStatusColor[request.status] || null,
        assignee: assigneeName(request),
        deadlineText: deadline.text,
        overdue: deadline.overdue,
        hasUnread: request.hasUnread === true,
    };
}

/**
 * Номера, которые поиск не должен предлагать: текущее обращение и уже связанные с ним.
 * Остальные ограничения (цикл, второй уровень, доступ) бэкенд проверяет сам и в поиске,
 * и при сохранении.
 *
 * @param {number} currentId
 * @param {Object[]} children
 * @param {Object[]} candidates
 * @returns {Object[]}
 */
export function filterCandidates(currentId, children, candidates) {
    const excluded = new Set([currentId, ...(Array.isArray(children) ? children : []).map((child) => child.id)]);

    return (Array.isArray(candidates) ? candidates : []).filter((candidate) => !excluded.has(candidate.id));
}
